import { Head, router } from '@inertiajs/react'
import { DocumentChartBarIcon } from '@heroicons/react/24/outline'
import { withAppLayout, PageHeading } from '@/components/layout'
import { Button, Table, TableHead, TableBody, TableRow, TableHeader, TableCell } from '@/components/ui'
import { StatusPill } from '@/components/common/StatusPill'
import { EmptyState } from '@/components/common/EmptyState'
import { textLinkClasses } from '@/components/common/TextLink'
import { cn } from '@/lib/utils'
import { useTranslations } from '@/lib/i18n'
import { formatDateTime } from '@/lib/format'
import type { StatusFamily } from '@/types'

interface ExportRow {
  id: number
  kind: 'payments' | 'payouts'
  format: string
  status: 'pending' | 'processing' | 'completed' | 'failed'
  row_count: number | null
  merchant_name: string
  created_at: string
  ready: boolean
  download_path: string
}

const STATUS_FAMILY: Record<ExportRow['status'], StatusFamily> = {
  pending: 'neutral',
  processing: 'pending',
  completed: 'success',
  failed: 'failed',
}

function ExportsIndex({ exports }: { exports: ExportRow[] }) {
  const { t, locale } = useTranslations()

  return (
    <>
      <Head title={t('exports.title')} />
      <PageHeading
        title={t('exports.title')}
        subtitle={t('exports.subtitle')}
        actions={
          <Button outline onClick={() => router.reload({ only: ['exports'] })}>
            {t('common.refresh')}
          </Button>
        }
      />

      <div className="mt-6">
        {exports.length === 0 ? (
          <EmptyState icon={DocumentChartBarIcon} title={t('exports.empty')} />
        ) : (
          <Table>
            <TableHead>
              <TableRow>
                <TableHeader>{t('exports.requested')}</TableHeader>
                <TableHeader>{t('exports.type')}</TableHeader>
                <TableHeader>{t('exports.format')}</TableHeader>
                <TableHeader>{t('payments.columns.status')}</TableHeader>
                <TableHeader className="text-right">{t('exports.rows')}</TableHeader>
                <TableHeader className="text-right">{t('common.actions')}</TableHeader>
              </TableRow>
            </TableHead>
            <TableBody>
              {exports.map((row) => (
                <TableRow key={row.id}>
                  <TableCell className="whitespace-nowrap">
                    <div>{formatDateTime(row.created_at, locale)}</div>
                    <div className="text-text/60">{row.merchant_name}</div>
                  </TableCell>
                  <TableCell>{t(`nav.${row.kind}`)}</TableCell>
                  <TableCell className="uppercase">{row.format}</TableCell>
                  <TableCell>
                    <StatusPill family={STATUS_FAMILY[row.status]}>{t(`exports.statuses.${row.status}`)}</StatusPill>
                  </TableCell>
                  <TableCell className="text-right">{row.row_count ?? '—'}</TableCell>
                  <TableCell className="text-right">
                    {row.ready ? (
                      <a href={row.download_path} className={cn(textLinkClasses, 'text-sm')}>
                        {t('exports.download')}
                      </a>
                    ) : (
                      <span className="text-text/40">—</span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </>
  )
}

ExportsIndex.layout = withAppLayout

export default ExportsIndex
